import { ParentSize } from "@visx/responsive";
import { Group } from "@visx/group";
import { motion, AnimatePresence } from "framer-motion";
import { useMemo, useRef, useState } from "react";
import type { VINode, VIEdge } from "@/app/(articles)/(learn)/solved/ValueIterationSim.js";
import { ValueIterationSim } from "@/app/(articles)/(learn)/solved/ValueIterationSim.js";
import { cn } from "@rosette/ts/src/util/cn.js";
import { useAnimationTimer } from "@rosette/web_utils/src/useAnimationTimer.js";
import { DeviceInfoProvider, useIsMobile } from "@rosette/web_components/src/accessibility/DeviceInfoContext.js";
import { abs, clamp } from "@rosette/ts/src/util/numbers.js";

/* ----------  constants  ---------- */

const NODE_R = 16;
const EDGE_GAP = 6;
const ARROW_HEAD_SIZE = 6;
const FRAME_MS = 500;
const PACKET_MS = 500;

const FADE_DURATION_S = 0.2;
const FADE_DELAY_S = 0.4;

/* ----------  colour helpers  ---------- */

function valueColour(value: number): string {
    if (isNaN(value))
        return "rgb(0, 0, 0)";

    // Stretch [20, 80] out to [0, 100]
    value = 100.0 * clamp((value - 20) / 60, 0, 1);

    const hue = (value / 100) * 132;
    const saturation = 50 + 20 * abs(value - 50) / 50;
    const lightness = 50 - 10 * (value / 100) - 10 * clamp(1.0 - abs(value - 40) / 20, 0, 1);
    return `hsl(${hue},${saturation}%,${lightness}%)`;
}

function packetColour(delta: number): string {
    const t = Math.max(-5, Math.min(5, delta)) / 10 + 0.5; // map [-5,5]→[0,1]
    return valueColour(t * 100);
}

function nodeOpacity(node: VINode): number {
    let opacity;
    if (isNaN(node.value)) {
        opacity = 0;
    } else if (node.totalUpdates >= 1) {
        opacity = (node.updates >= 1 ? 1 : 0);
    } else {
        opacity = 1;
    }
    return Math.max(0.2, Math.min(1, opacity));
}

/* ----------  nodes + edges  ---------- */

interface NodeProps {
    node: VINode;
}

function NodeCircle({ node }: NodeProps) {
    const value = (node.totalUpdates > 0 && node.updates === 0 ? NaN : node.value);
    const fill = valueColour(value);
    const opacity = nodeOpacity(node);
    return (
        <motion.g
            initial={{ opacity: 0 }}
            animate={{ opacity }}
            exit={{ opacity: 0 }}
            transition={{
                duration: FADE_DURATION_S,
                ease: "easeOut" as const,
                delay: FADE_DELAY_S * node.y / 400,
            }}>

            <motion.circle
                cx={node.x}
                cy={node.y}
                r={node.radius}
                fill={fill}
                className="stroke-text-strong"
                strokeWidth={2} />

            {!isNaN(value) && (
                <text
                    x={node.x}
                    y={node.y + 2}
                    className="fill-text-strong"
                    fontSize={12}
                    textAnchor="middle"
                    dominantBaseline="middle">

                    {Math.round(value)}
                </text>
            )}
        </motion.g>
    );
}


interface EdgeProps {
    edge: VIEdge;
}


function EdgeLine({ edge }: EdgeProps) {
    const { x1, y1, x2, y2 } = endpoints(edge.to, edge.from);
    const opacity = Math.min(nodeOpacity(edge.from), nodeOpacity(edge.to));
    return (
        <motion.line
            x1={x1}
            y1={y1}
            x2={x2}
            y2={y2}
            className="stroke-text-strong"
            strokeWidth={2}
            markerEnd="url(#arrowHead)"
            vectorEffect="non-scaling-stroke"
            initial={{ opacity: 0 }}
            animate={{ opacity }}
            exit={{ opacity: 0 }}
            transition={{
                duration: FADE_DURATION_S,
                ease: "easeOut" as const,
                delay: FADE_DELAY_S * ((y1 + y2) / 2) / 400,
            }} />
    );
}

interface PacketProps {
    from: VINode;
    to: VINode;
    delta: number;
}

function Packet({ from, to, delta }: PacketProps) {
    const { x1, y1, x2, y2 } = endpoints(from, to);
    const duration = PACKET_MS / 1000;
    return (
        <motion.circle
            cx={x1}
            cy={y1}
            r={5}
            className="stroke-text-strong"
            strokeWidth={1}
            fill={packetColour(delta)}
            initial={{ cx: x1, cy: y1, opacity: 1 }}
            animate={{ cx: x2, cy: y2, opacity: 0 }}
            transition={{
                cx: { duration, ease: "linear" as const },
                cy: { duration, ease: "linear" as const },
                opacity: {
                    duration: duration / 2,
                    ease: "easeIn" as const,
                    delay: duration / 2,
                },
            }} />
    );
}

/* ----------  helpers  ---------- */

function endpoints(a: VINode, b: VINode) {
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const len = Math.hypot(dx, dy) || 1;
    const ux = dx / len;
    const uy = dy / len;
    return {
        x1: a.x + ux * (a.radius + EDGE_GAP - ARROW_HEAD_SIZE),
        y1: a.y + uy * (a.radius + EDGE_GAP - ARROW_HEAD_SIZE),
        x2: b.x - ux * (b.radius + EDGE_GAP),
        y2: b.y - uy * (b.radius + EDGE_GAP),
    };
}

function createNewSim(
    width: number,
    height: number,
    nodeCount: number,
    nodeRadius: number,
    importantNodeRadius: number,
    border: number,
): ValueIterationSim {
    let remainingTries = 10;
    while (remainingTries > 0) {
        try {
            // This fails occasionally due to bad luck in the graph generation.
            return new ValueIterationSim(nodeCount, width, height, nodeRadius, importantNodeRadius, border);
        } catch (_ignored) {
            remainingTries -= 1;
        }
    }
    return new ValueIterationSim(nodeCount, width, height, nodeRadius, importantNodeRadius, border);
}

/* ----------  main component  ---------- */

interface InternalValueIterationVisualisationProps {
    width: number;
    height: number;
}

export function InternalValueIterationVisualisation({
    width,
    height,
}: InternalValueIterationVisualisationProps) {
    const isMobile = useIsMobile();
    const nodeCount = (isMobile ? 16 : 24);

    /* ----------  simulation + frame state  ---------- */
    const [sim, setSim] = useState<ValueIterationSim | null>(null);
    const [frameNumber, setFrameNumber] = useState(0);

    const lastSimRef = useRef<ValueIterationSim | null>(null);
    if (sim !== null) {
        lastSimRef.current = sim;
    }

    /* ----------  animation frame control  ---------- */
    const update = useMemo(() => {
        let localSim: ValueIterationSim | null = null;
        let localFrame = 0;
        let pauseFrames = 0;

        return () => {
            if (!localSim) {
                if (width < 10 || height < 10) return;

                pauseFrames -= 1;
                if (pauseFrames <= 0) {
                    const importantR = (isMobile ? 1.25 : 1.5) * NODE_R;
                    const border = (isMobile ? 6 : 32);
                    localSim = createNewSim(width, height, nodeCount, NODE_R, importantR, border);
                    localFrame = 0;
                    pauseFrames = 0;
                    setSim(localSim);
                    setFrameNumber(localFrame);
                }
                return;
            }

            const frames = localSim.animation;
            if (localFrame >= frames.length) {
                if (pauseFrames >= 0) {
                    pauseFrames -= 1;
                } else {
                    localSim = null;
                    setSim(null);
                    setFrameNumber(0);
                }
            } else {
                if (pauseFrames >= 0) {
                    pauseFrames -= 1;
                } else {
                    localFrame += 1;
                    if (localFrame >= frames.length) {
                        pauseFrames = 2;
                    }
                }
                setFrameNumber(localFrame);
            }
        };
        // Re‑create only if the static inputs change
    }, [width, height, nodeCount, isMobile]);

    const animRef = useAnimationTimer(update, FRAME_MS);

    /* ----------  current frame data  ---------- */
    const frames = sim ? sim.animation : null;
    const frame = frames && frames.length && frameNumber >= 0
            ? frames[Math.min(frameNumber, frames.length - 1)]
            : null;

    if (sim && frame) {
        const states = frame.nodeStates;
        sim.nodes.forEach((n) => {
            const state = states[n.id];
            n.value = state.value;
            n.updates = state.updates;
        });
    }

    /* ----------  render  ---------- */
    let nodes: VINode[];
    if (sim) {
        nodes = sim.nodes;
    } else if (lastSimRef.current) {
        const lastSim = lastSimRef.current;
        nodes = [lastSim.win, lastSim.lose];
    } else {
        nodes = [];
    }

    const nodesById: Record<number, VINode> = {};
    for (const n of nodes) {
        nodesById[n.id] = n;
    }

    const edges = sim?.edges ?? [];
    const packets = frame?.packets ?? [];

    return (
        <svg width={width} height={height} ref={animRef}>
            <defs>
                <marker
                    id="arrowHead"
                    viewBox="0 0 10 10"
                    refX="5"
                    refY="5"
                    markerWidth="6"
                    markerHeight="6"
                    orient="auto">
                    <path d="M 0 0 L 10 5 L 0 10 z" className="fill-text-strong" />
                </marker>
            </defs>

            <Group>
                <AnimatePresence>
                    {/* edges */}
                    {edges.map(edge => (
                        <EdgeLine key={`edge-${edge.id}`} edge={edge} />
                    ))}

                    {/* packets */}
                    {packets.map((p, idx) => {
                        const from = nodesById[p.fromId];
                        const to = nodesById[p.toId];
                        if (!from || !to) return null;
                        return (
                            <Packet
                                key={`packet-${frameNumber}-${idx}`}
                                from={from}
                                to={to}
                                delta={p.valueDelta} />
                        );
                    })}

                    {/* nodes */}
                    {nodes.map(node => (
                        <NodeCircle
                            key={`node-${node.id}`}
                            node={node} />
                    ))}
                </AnimatePresence>
            </Group>
        </svg>
    );
}

interface ValueIterationVisualisationProps {
    className?: string;
    nodeCount?: number;
}

export function ValueIterationVisualisation({
    className,
}: ValueIterationVisualisationProps) {
    return (
        <DeviceInfoProvider>
            <figure className={cn("mt-0 mb-0", className ?? null)}>
                <div className="w-full h-48 md:h-64 select-none">
                    <ParentSize>
                        {({ width, height }) => (
                            <InternalValueIterationVisualisation
                                width={width}
                                height={height} />
                        )}
                    </ParentSize>
                </div>
                <figcaption>
                    Watch value flow backwards through the graph, mimicking value iteration.
                </figcaption>
            </figure>
        </DeviceInfoProvider>
    );
}
